import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { AlertChild, AlertHistoryChild } from '@kcftech/prometheus-client';
import AcknowledgeAlert from 'assets/AcknowledgeAlert.svg?react';
import { Button } from 'components/button/Button';
import { GroupHierarchyText } from 'components/group_hierarchy/GroupHierarchyText';
import { SvgIcon } from 'components/svg_icon/SvgIcon';
import {
    AlertInfo,
    AlertInfoWrapper,
    ChildAlertContainer,
    Description,
    ExternalLinkIcon,
    OverlayLink,
    SeverityLozenge,
    Timestamp,
    TooltipArea,
} from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/styled';
import {
    formatAlertTimestamp,
    getWorkbenchUrl,
} from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/utils';
import { useRef } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import {
    getAlertSeverity,
    getStandardizedSeverity,
    SEVERITY_LABEL_MAP,
} from 'utils/alert_severity';

const StyledSvgIcon = styled(SvgIcon)`
    color: ${tokens.icon.actionActive};

    &:hover {
        color: ${tokens.icon.actionHover};
    }
`;

type ChildAlertProps = {
    alert: AlertChild | AlertHistoryChild;
    onAcknowledge?: () => void;
    isAcknowledging?: boolean;
    formatDate: (date: Date) => string;
    formatTime: (date: Date) => string;
};

export function ChildAlert({
    alert,
    formatDate,
    formatTime,
    onAcknowledge,
    isAcknowledging,
}: ChildAlertProps) {
    const childSeverity = getAlertSeverity(alert.AlertType);
    const standardSeverity = getStandardizedSeverity(childSeverity);
    const childSeverityLabel = SEVERITY_LABEL_MAP[standardSeverity];
    const linkRef = useRef<HTMLAnchorElement>(null);

    const handleTooltipClick = (event: React.MouseEvent) => {
        // Stop the event from propagating to prevent double navigation
        event.stopPropagation();
        // Programmatically click the underlying link
        linkRef.current?.click();
    };

    return (
        <div className="flex justify-between">
            <ChildAlertContainer>
                <AlertInfoWrapper>
                    <OverlayLink
                        ref={linkRef}
                        to={getWorkbenchUrl(alert.Indicator.Hierarchy, alert.AlertId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View trend for ${alert.Indicator.IndicatorName}`}
                    />
                    <AlertInfo>
                        <SeverityLozenge $severity={childSeverity}>
                            {childSeverityLabel}
                        </SeverityLozenge>
                        <Description>
                            <TooltipArea
                                className="description-tooltip-area"
                                onClick={handleTooltipClick}
                            >
                                <GroupHierarchyText
                                    suffixClassName="child-alert-indicator-text"
                                    hierarchy={alert.Indicator.Hierarchy}
                                    suffix={alert.Indicator.IndicatorName}
                                />
                            </TooltipArea>
                            <ExternalLinkIcon icon={faExternalLinkAlt} />
                        </Description>
                    </AlertInfo>
                    <Timestamp>
                        {formatAlertTimestamp(
                            formatDate,
                            formatTime,
                            new Date(alert.AlertTime),
                        )}
                    </Timestamp>
                </AlertInfoWrapper>
            </ChildAlertContainer>
            {alert instanceof AlertHistoryChild &&
                !alert.IsAcknowledged &&
                onAcknowledge && (
                    <div style={{ alignSelf: 'flex-start' }}>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAcknowledge();
                            }}
                            disabled={isAcknowledging}
                            aria-label="Acknowledge alert"
                        >
                            <StyledSvgIcon component={AcknowledgeAlert} size={22} />
                        </Button>
                    </div>
                )}
        </div>
    );
}
